/**
 * GoogieToday — Today, in the Googie v7 language.
 *
 * GOOGIE-1, board "7a · Today". The screen the canvas leads with:
 * the studio's own sky behind a greeting that follows the clock, a
 * row of quick actions on circular Googie badges, this week's four
 * numbers with the alarming one filled in orange, the active projects
 * as rows with a stage chip and a progress line, and a right-hand
 * column carrying Nova's latest nudge, the month's goal rings and the
 * long view.
 *
 * Every number is the studio's real data — the same hooks the current
 * dashboard reads. The one thing the canvas invents and we do not is
 * Nova's face: her card here is typographic, and the Nova character
 * stays exactly as it ships on /assistant.
 *
 * SEASON-1 — in a season the quick actions and this week's numbers wear
 * its colours, and for its last week the greeting is the season's own
 * ("Happy Halloween, Rennan."), with the boards' offset echo behind it.
 * The status and stage hues don't change: they mean something.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, Clock, Mail, Telescope } from 'lucide-react';
import { NovaIcon } from '@/components/nova';
// GOOGIE-5 — the action rail wears the redesign's own marks, not
// lucide's modern glyphs. Rennan sent the reference; see icons.tsx.
import { GAlarmClock, GAtom, GReceipt, GRobot, GRocket, GSunburst, GTelevision } from '../icons';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/services/projects';
import { useInquiries } from '@/services/inquiries';
import { useGamification } from '@/hooks/useGamification';
import { PassportTodayCard, isPassportPreviewState } from '@/components/dashboard/PassportTodayCard';
import { ActivationCard } from '@/components/dashboard/ActivationCard';
import { TodaySchedule } from '../TodaySchedule';
import { FoundingPassCard } from '@/components/dashboard/FoundingPassCard';
import { track } from '@/lib/analytics';
import { useClients } from '@/services/clients';
import { G, ORBIT_BLUE, onColor } from '@/lib/googie';
import { SEASON_GREETING, SEASON_PALETTE, seasonAccents, seasonGreeting } from '@/lib/seasons';
import { useSeasonPreview } from '@/hooks/useSeasonalThemes';
import { GoogieScreen, useGoogieSky } from '../GoogieShell';
import {
  GoogieAvatar,
  GoogieChip,
  GoogieHeader,
  GoogiePage,
  GoogieSectionTitle,
  GoogieStat,
  GoogieTag,
} from '../primitives';
import { InquiryModal } from '@/components/crm/InquiryModal';
import { useDeleteInquiry, useInquiryStats } from '@/services/inquiries';
import type { Inquiry, InquiryStatus } from '@/types/crm';
import { asDay } from '@/lib/dates';

const STAGE_COLOR: Record<string, string> = {
  discovery: G.sky,
  planning: G.sky,
  shooting: '#E8622C',
  editing: G.violet,
  review: G.mustard,
  delivered: G.green,
  completed: G.green,
};

/** Lead-status hues — the same three the Inquiries screen uses, so a
 *  lead is the same colour on both screens. */
const STATUS_HUE: Record<string, string> = {
  pending: G.mustard,
  accepted: '#7FB069',
  follow_up: '#E8622C',
  in_progress: G.cyan,
  completed: G.lime,
  closed_lost: '#B8B2A6',
};

export function GoogieToday() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // GOOGIE-2 — the sky belongs to the shell now (RootLayout wraps
  // every route in it), so the chip reaches for it rather than
  // standing up a shell of its own.
  const { sky, season } = useGoogieSky();
  // SEASON-1 — the season's accents (the year-round ones without one).
  const accents = seasonAccents(season);
  // A preview shows the season at its most festive, greeting included,
  // so it can be reviewed outside the last week (same as iOS).
  const previewing = useSeasonPreview() !== null;
  const greeting = (season && (previewing ? SEASON_GREETING[season] : seasonGreeting(season))) || sky.greet;
  const echo = season ? SEASON_PALETTE[season].echo : null;
  const { data: projects = [] } = useProjects();
  const { data: inquiries = [] } = useInquiries();
  const { data: clients = [] } = useClients();
  const [gamificationOn] = useGamification();
  // DEVMODE-1 — `/?passport=fresh|progress|reachable|stamped|empty` and
  // `/?setup=1` show the first-run Today states from Admin → Preview.
  const [searchParams] = useSearchParams();
  const passportParam = searchParams.get('passport');
  const passportPreview = isPassportPreviewState(passportParam) ? passportParam : undefined;
  const setupPreview = searchParams.get('setup') === '1';

  // FREEWEEK-1 — one 'today_viewed' per tab session: how many people who
  // got through setup come back to the app at all.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem('orbit_today_seen') === '1') return;
      window.sessionStorage.setItem('orbit_today_seen', '1');
    } catch {
      /* private mode — count it anyway */
    }
    track('today_viewed');
  }, []);
  // GOOGIE-3 — the pipeline is back. Rennan's line was "we need to
  // bring back the pipeline just refit with the new design": the
  // dashboard has always ended in the lead pipeline, and a Today
  // screen that drops it makes the studio open a second page to
  // answer the only question Today is for. Same counter the
  // Inquiries page reads, so the two can never disagree.
  const pipeline = useInquiryStats('all');
  const deleteInquiry = useDeleteInquiry();
  const [lens, setLens] = useState<InquiryStatus | 'all'>('all');
  const [selected, setSelected] = useState<Inquiry | null>(null);

  const firstName = useMemo(() => {
    const meta = (user?.user_metadata ?? {}) as { full_name?: string; name?: string };
    const full = meta.full_name || meta.name || user?.email || '';
    return full.split(/[\s@]/)[0] || 'there';
  }, [user]);

  const active = useMemo(
    () =>
      (projects as Array<Record<string, unknown>>).filter(
        (p) => String(p.status ?? '') !== 'completed' && String(p.status ?? '') !== 'archived',
      ),
    [projects],
  );

  // "Idle" is the number the canvas paints in alarm orange: a lead
  // that came in more than three days ago and is still pending.
  const idle = useMemo(() => {
    const cut = Date.now() - 3 * 24 * 60 * 60 * 1000;
    return inquiries.filter((i) => i.status === 'pending' && new Date(i.submittedAt).getTime() < cut).length;
  }, [inquiries]);

  const dueThisWeek = useMemo(() => {
    const now = Date.now();
    const week = now + 7 * 24 * 60 * 60 * 1000;
    return (projects as Array<Record<string, unknown>>).filter((p) => {
      const raw = (p.due_date ?? p.dueDate) as string | undefined;
      if (!raw) return false;
      const t = asDay(raw).getTime();
      return t >= now && t <= week;
    }).length;
  }, [projects]);

  const shootsToday = useMemo(() => {
    const today = new Date().toDateString();
    return (projects as Array<Record<string, unknown>>).filter((p) => {
      const raw = (p.scheduled_date ?? p.scheduledDate) as string | undefined;
      return raw ? new Date(raw).toDateString() === today : false;
    }).length;
  }, [projects]);

  const accepted = inquiries.filter((i) => i.status === 'accepted').length;
  const acceptRate = inquiries.length > 0 ? Math.round((accepted / inquiries.length) * 100) : 0;
  const thisMonth = useMemo(() => {
    const d = new Date();
    return inquiries.filter((i) => {
      const s = new Date(i.submittedAt);
      return s.getMonth() === d.getMonth() && s.getFullYear() === d.getFullYear();
    }).length;
  }, [inquiries]);

  const today = new Date();
  const dateLine = today.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  // The pipeline's own rows: newest first, filtered by the tile.
  const leads = useMemo(() => {
    const rows = lens === 'all' ? inquiries : inquiries.filter((i) => i.status === lens);
    return [...rows].sort(
      (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime(),
    );
  }, [inquiries, lens]);

  const handleDelete = async (id: string) => {
    await deleteInquiry.mutateAsync(id);
    setSelected(null);
  };

  const actions: Array<{
    label: string;
    icon: typeof GRocket;
    hue: string;
    go: () => void;
    primary?: boolean;
  }> = [
    { label: 'New project', icon: GRocket, hue: accents.mustard, go: () => navigate('/projects'), primary: true },
    { label: 'Add client', icon: GAtom, hue: accents.teal, go: () => navigate('/clients?new=1') },
    { label: 'New gallery', icon: GTelevision, hue: accents.orange, go: () => navigate('/galleries') },
    { label: 'Log expense', icon: GSunburst, hue: accents.lime, go: () => navigate('/expenses') },
    { label: 'Ask Nova', icon: GRobot, hue: accents.cyan, go: () => navigate('/assistant') },
    { label: 'Calendar', icon: GAlarmClock, hue: G.sky, go: () => navigate('/calendar') },
    // INVOICE-1 — Rennan asked for this stop by name: "adding the
    // colored button on the dashboard and the ios app today page as
    // a shortcut". Violet because the other six are taken and money
    // should not share a colour with expenses, which is lime.
    { label: 'Invoices', icon: GReceipt, hue: G.violet, go: () => navigate('/invoices') },
  ];

  return (
    <GoogieScreen>
      {/* GOOGIE-10 — no capsule. The chip here read "NIGHT · 6:59 PM",
          which is a clock and a label for a sky the studio can already
          see; it cycled the time of day on tap, which is a design
          toy, not a feature. The greeting below still follows the
          clock. */}
      <GoogieHeader />

      <GoogiePage>
        <div className="g-rise pt-8">
          <div className="g-eyebrow">{dateLine}</div>
          {/* SEASON-1 — the boards' festive letterform: the same line
              again, 4px down and right, in the season's colour at 60%.
              Same width as the headline, so it wraps where it wraps. */}
          <div className="relative mt-2">
            {echo && (
              <span
                aria-hidden
                className="g-h1 pointer-events-none absolute left-[4px] top-[4px] block w-full select-none forced-colors:hidden"
                style={{ color: echo, opacity: 0.6 }}
              >
                {greeting}, {firstName}.
              </span>
            )}
            <h1 className="g-h1 relative">
              {greeting}, {firstName}.
            </h1>
          </div>
        </div>

        {/* Quick actions — the Googie badge row. */}
        <div className="g-scroll-x -mx-2 mt-6 flex gap-2 px-2 pb-1">
          {/* GOOGIE-11 — the rail is filled now. Each action wears its
              own colour rather than outlining it, the disc inverts to
              carry the mark, and every mark is Orbit's blue: six
              filled pills in six colours could read as six unrelated
              buttons, and one blue icon in all of them is what makes
              it read as one control with six stops. The label takes
              ink or ivory depending on what can be read on the fill —
              lime and cyan need ink, teal and orange need ivory. */}
          {actions.map((a) => {
            const fill = a.primary ? accents.mustard : a.hue;
            return (
              <button
                key={a.label}
                type="button"
                onClick={a.go}
                className="g-pill"
                style={{ background: fill, borderColor: 'transparent', color: onColor(fill) }}
              >
                <span
                  className="g-badge"
                  style={{ width: 40, height: 40, background: '#F7F3EC', outlineColor: 'rgba(255,255,255,.55)' }}
                >
                  <a.icon className="h-[22px] w-[22px]" style={{ color: ORBIT_BLUE }} />
                </span>
                {a.label}
              </button>
            );
          })}
        </div>

        {/* FREEWEEK-1 / OPEN-1 — for a founding member, their Pass until
            it closes. Nothing here otherwise. */}
        <div className="g-rise mt-7 empty:hidden">
          <FoundingPassCard />
        </div>

        {/* SIGNUP-5 — the Passport capsule from the prototype sits right
            under the greeting: the studio's world, the ring that needs
            love, what fills it next. Gold + Launch when every ring is
            full; the day's stamp after Nova lands. Replaces the old
            right-column rings card (same data, one place). */}
        {(gamificationOn || passportPreview) && (
          <div className="g-rise mt-7">
            <PassportTodayCard preview={passportPreview} />
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,1fr)_400px]">
          {/* ── Left column ─────────────────────────────────── */}
          <div>
            {/* SIGNUP-5 — "A few things to start with": the setup
                checklist, restyled to the prototype's glass panel. Hides
                itself once the studio is activated, folds to a pill
                after three steps. */}
            <div className="mb-10 empty:hidden">
              <ActivationCard collapsible forceShow={setupPreview} />
            </div>
            <GoogieSectionTitle
              action={<span className="g-th">{weekLabel()}</span>}
            >
              This week
            </GoogieSectionTitle>
            <div className="flex flex-wrap gap-4">
              <GoogieStat to="/calendar" value={shootsToday} label="Today's shoots" icon={<Camera className="h-5 w-5" style={{ color: accents.cyan }} />} accent={accents.cyan} />
              <GoogieStat to="/projects" value={dueThisWeek} label="Due this week" icon={<Clock className="h-5 w-5" style={{ color: G.sky }} />} accent={G.sky} />
              <GoogieStat
                to="/inquiries"
                value={idle}
                label="Idle leads · 3+ days"
                icon={<Mail className="h-5 w-5" style={{ color: 'var(--g-on-accent)' }} />}
                accent={accents.orange}
                filled={idle > 0}
              />
              <GoogieStat to="/projects" value={active.length} label="Active projects" icon={<Telescope className="h-5 w-5" style={{ color: accents.lime }} />} accent={accents.lime} />
            </div>

            <div className="mt-12">
              <GoogieSectionTitle
                count={active.length}
                action={
                  <Link to="/projects" className="g-link text-[15px] font-semibold" style={{ color: accents.cyan }}>
                    View all →
                  </Link>
                }
              >
                Active projects
              </GoogieSectionTitle>
              <div className="flex flex-col gap-2.5">
                {active.slice(0, 6).map((p) => {
                  const row = p as Record<string, unknown>;
                  const client = (row.client ?? null) as { name?: string } | null;
                  const status = String(row.status ?? 'discovery');
                  const hue = STAGE_COLOR[status] ?? G.sky;
                  const due = (row.due_date ?? row.dueDate) as string | undefined;
                  return (
                    <Link key={String(row.id)} to={`/studio/${String(row.id)}`} className="g-row no-underline" style={{ color: 'inherit' }}>
                      <GoogieAvatar name={client?.name ?? String(row.name ?? '')} hue={hue} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[17.5px] font-semibold">{String(row.name ?? 'Untitled')}</span>
                        {client?.name && <span className="g-mute block truncate text-[15px]">{client.name}</span>}
                      </span>
                      <span aria-hidden className="hidden h-1.5 w-[120px] flex-none overflow-hidden rounded-full md:block" style={{ background: 'var(--g-track)' }}>
                        <span className="block h-full rounded-full" style={{ width: `${stageProgress(status)}%`, background: hue }} />
                      </span>
                      <GoogieChip>
                        {status.replace('_', ' ')}
                        {due ? ` · ${asDay(due).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
                      </GoogieChip>
                    </Link>
                  );
                })}
                {active.length === 0 && (
                  <div className="g-card px-6 py-10 text-center">
                    <p className="g-mute text-[16px]">No active projects. Start one and it shows up here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Right column ────────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {/* CAL-PRO-4 — the day itself, above Nova. This page had
                no idea what was on the studio's calendar: "Today's
                shoots" below is counted off project dates, so three
                blocks and a client booking showed up nowhere. */}
            <TodaySchedule />
            <Link to="/assistant" className="g-card block p-7 no-underline" style={{ color: 'inherit' }}>
              <div className="flex items-center gap-3">
                {/* GOOGIE-13 — Nova herself, not a sparkle. The card is
                    the only place on Today that speaks for her, and a
                    lucide glyph made it read as a generic "AI" tile.
                    This is the app's canonical 2026 mark (NovaIcon →
                    NovaExpression), so the face here and the face on
                    /assistant are the same character — which is the
                    whole reason the redesign never redrew her. */}
                <span
                  className="g-badge"
                  style={{ width: 44, height: 44, background: accents.teal, outlineColor: 'var(--g-track)' }}
                >
                  <NovaIcon className="h-7 w-7" />
                </span>
                <span>
                  <span className="g-h3 block">Ask Nova</span>
                  <span className="g-mute block text-[14px]">Always on</span>
                </span>
              </div>
              <p className="mt-4 text-[16px] leading-relaxed">
                {idle > 0
                  ? `${idle} ${idle === 1 ? 'lead has' : 'leads have'} gone quiet for three days or more. Want the follow-ups drafted?`
                  : 'Ask for a brief on the week, a client email, or anything you would otherwise open four tabs for.'}
              </p>
              <span className="g-pill g-pill-solid mt-4">Open Nova</span>
            </Link>

            <div className="g-card p-7">
              <h3 className="g-h3">The long view</h3>
              <div className="mt-4 grid grid-cols-2 gap-y-5">
                <Figure value={inquiries.length} label="All-time inquiries" />
                <Figure value={`${acceptRate}%`} label="Acceptance rate" />
                <Figure value={thisMonth} label="This month" />
                <Figure value={active.length} label="In progress" />
              </div>
            </div>
          </div>
        </div>

        {/* ── The pipeline ──────────────────────────────────────
            Restored in GOOGIE-3. Four tiles that filter the list
            under them, and the leads themselves as rows — the same
            move the old dashboard made, in the redesign's shapes.
            Tapping a lead opens the app's real inquiry sheet. */}
        <div className="mt-16">
          <GoogieSectionTitle
            count={inquiries.length}
            action={
              <Link to="/inquiries" className="g-link text-[15px] font-semibold" style={{ color: accents.orange }}>
                Open the pipeline →
              </Link>
            }
          >
            Pipeline
          </GoogieSectionTitle>

          <div className="flex flex-wrap gap-4">
            {(
              [
                ['all', 'All leads', G.cyan, inquiries.length],
                ['pending', 'Pending', G.mustard, pipeline.pending],
                ['accepted', 'Accepted', G.green, pipeline.accepted],
                ['follow_up', 'Follow up', '#E8622C', pipeline.followUp],
              ] as const
            ).map(([key, label, tone, value]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLens(key as InquiryStatus | 'all')}
                aria-pressed={lens === key}
                className="g-stat g-card-sm min-w-[150px] flex-1 px-6 py-5 text-left transition-transform hover:-translate-y-0.5"
                style={
                  lens === key
                    ? { background: 'var(--g-paper)', borderColor: tone }
                    : undefined
                }
              >
                <div className="g-display text-[35px]" style={{ color: tone }}>
                  {value}
                </div>
                <div className="g-mute mt-1 text-[15px]">{label}</div>
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-2.5">
            {leads.slice(0, 6).map((lead) => (
              <button
                key={lead.id}
                type="button"
                onClick={() => setSelected(lead)}
                className="g-row w-full text-left"
              >
                <GoogieAvatar name={lead.name} hue={STATUS_HUE[lead.status] ?? G.sky} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] font-semibold">{lead.name}</span>
                  <span className="g-mute block truncate text-[15px]">
                    {lead.email}
                    {lead.projectDate ? ` · ${new Date(lead.projectDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
                  </span>
                </span>
                <GoogieTag color={STATUS_HUE[lead.status] ?? G.sky}>
                  {lead.status.replace('_', ' ')}
                </GoogieTag>
              </button>
            ))}
            {leads.length === 0 && (
              <div className="g-card px-6 py-10 text-center">
                <p className="g-mute text-[16px]">
                  {inquiries.length === 0
                    ? 'No leads yet. Share your intake form and the first one lands here.'
                    : 'Nothing in that stage right now.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </GoogiePage>

      {/* The app's own lead sheet — one editor for a lead, not two.
          The sheet writes through its own mutation; these callbacks
          only close it, exactly as the Inquiries screen does. */}
      <InquiryModal
        inquiry={selected}
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
        onUpdate={() => setSelected(null)}
        onDelete={handleDelete}
      />
    </GoogieScreen>
  );
}

function Figure({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div>
      <div className="g-display text-[35px]">{value}</div>
      <div className="g-mute text-[15px]">{label}</div>
    </div>
  );
}

/** Roughly where a project sits, by stage — the canvas's progress line. */
function stageProgress(status: string): number {
  const order = ['discovery', 'planning', 'shooting', 'editing', 'review', 'delivered', 'completed'];
  const i = order.indexOf(status);
  return i < 0 ? 20 : Math.round(((i + 1) / order.length) * 100);
}

function weekLabel(): string {
  const now = new Date();
  const end = new Date(now.getTime() + 6 * 24 * 60 * 60 * 1000);
  const f = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${f(now)} – ${f(end)}`;
}

export default GoogieToday;
